import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const statusOptions = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "pilot_ready", label: "Pilot Ready" },
  { value: "invited", label: "Invited" },
];

export default function LeadEditorPanel({ lead, saving, onClose, onSave }) {
  const [status, setStatus] = useState("new");
  const [adminNotes, setAdminNotes] = useState("");
  const [adminTags, setAdminTags] = useState("");

  useEffect(() => {
    if (!lead) return;
    setStatus(lead.status || "new");
    setAdminNotes(lead.admin_notes || "");
    setAdminTags((lead.admin_tags || []).join(", "));
  }, [lead]);

  if (!lead) return null;
  const isPendingLead = lead.status === "pending_verification";

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      status,
      admin_notes: adminNotes,
      admin_tags: adminTags.split(",").map((item) => item.trim()).filter(Boolean),
    });
  };

  return (
    <Card className="rounded-[32px] border-[#3ABDBC]/20 bg-[#08133E]/88 shadow-[0_24px_70px_-45px_rgba(1,11,48,0.75)] backdrop-blur-xl" data-testid="lead-editor-card">
      <CardContent className="space-y-6 p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-sky-700" data-testid="lead-editor-label">Lead follow-up editor</p>
            <h3 className="mt-2 font-display text-3xl font-semibold text-slate-950" data-testid="lead-editor-title">{lead.full_name}</h3>
            <p className="mt-2 text-sm leading-7 text-slate-600" data-testid="lead-editor-subtitle">{lead.email} • {lead.country} • {lead.role}</p>
          </div>
          <Button type="button" variant="outline" className="h-11 rounded-full border-slate-300 bg-white/80" onClick={onClose} data-testid="lead-editor-close-button">
            Close
          </Button>
        </div>

        <form className="grid gap-5 lg:grid-cols-[240px_1fr]" onSubmit={handleSubmit} data-testid="lead-editor-form">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700" htmlFor="lead-status-select" data-testid="lead-status-label">
              Follow-up status
            </label>
            <select
              id="lead-status-select"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-white/90 px-4 text-sm text-slate-900"
              data-testid="lead-status-select"
            >
              {isPendingLead ? (
                <option value="pending_verification">Pending Verification</option>
              ) : (
                statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))
              )}
            </select>
            {isPendingLead && (
              <p className="text-xs leading-6 text-slate-500" data-testid="lead-status-pending-note">
                This lead can receive notes and tags now, but follow-up statuses unlock only after email verification is completed.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700" htmlFor="lead-tags-input" data-testid="lead-tags-label">
              Admin tags
            </label>
            <Input
              id="lead-tags-input"
              value={adminTags}
              onChange={(event) => setAdminTags(event.target.value)}
              placeholder="High intent, Nigeria, API, pilot"
              className="h-12 rounded-2xl border-slate-200 bg-white/90"
              data-testid="lead-tags-input"
            />
          </div>

          <div className="space-y-2 lg:col-span-2">
            <label className="text-sm font-semibold text-slate-700" htmlFor="lead-notes-textarea" data-testid="lead-notes-label">
              Follow-up notes
            </label>
            <Textarea
              id="lead-notes-textarea"
              value={adminNotes}
              onChange={(event) => setAdminNotes(event.target.value)}
              placeholder="Capture founder notes, outreach context, product needs, or pilot-readiness signals here."
              className="min-h-[160px] rounded-2xl border-slate-200 bg-white/90"
              data-testid="lead-notes-textarea"
            />
          </div>

          <div className="lg:col-span-2">
            <Button type="submit" className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800" disabled={saving} data-testid="lead-save-button">
              {saving ? "Saving..." : "Save Lead Updates"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}